(() => {
  const registry = new Map();
  const api = window.GooeyExperience || {};
  const STORAGE_KEY = 'gooeyExperience:v3';
  const STATE_VERSION = 3;

  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const clone = value => {
    if (value === undefined) return undefined;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return value;
    }
  };
  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };
  const productHandleFromUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      const match = String(value).match(/\/products\/([^/?#]+)/i);
      return match ? match[1] : '';
    }
  };
  const routes = () => window.GooeyDuck?.routes || {};
  const houseUrl = () => routes().root || '/';
  const scentFinderUrl = () => routes().scentFinder || `${routes().search || '/search'}?view=scent-finder`;

  const defaultState = () => ({
    version: STATE_VERSION,
    currentModule: null,
    currentRoom: null,
    currentSituation: null,
    currentProblem: null,
    selectedRecommendation: null,
    visitedRooms: [],
    scentInterests: [],
    scentMatch: null,
    continuation: null,
    lastInteraction: null
  });

  const loadState = () => {
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return defaultState();
      const parsed = JSON.parse(stored);
      if (!parsed || parsed.version !== STATE_VERSION) return defaultState();
      return { ...defaultState(), ...parsed };
    } catch (error) {
      return defaultState();
    }
  };

  let state = loadState();

  const persistState = () => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // The experience still works in memory when storage is unavailable.
    }
  };

  const emitStateChange = (previous, changed, meta = {}) => {
    document.dispatchEvent(new CustomEvent('gooey:experience:statechange', {
      detail: {
        state: clone(state),
        previous: clone(previous),
        changed,
        meta: clone(meta)
      }
    }));
  };

  api.state = {
    get() {
      return clone(state);
    },

    getValue(key) {
      return clone(state[key]);
    },

    patch(patch = {}, meta = {}) {
      if (!patch || typeof patch !== 'object') return clone(state);
      const previous = clone(state);
      const changed = Object.keys(patch);
      state = {
        ...state,
        ...clone(patch),
        version: STATE_VERSION,
        lastInteraction: {
          module: meta.module || patch.currentModule || state.currentModule || null,
          action: meta.action || 'state-update',
          at: Date.now()
        }
      };
      persistState();
      emitStateChange(previous, changed, meta);
      return clone(state);
    },

    set(key, value, meta = {}) {
      return this.patch({ [key]: value }, meta);
    },

    reset(meta = {}) {
      const previous = clone(state);
      state = defaultState();
      persistState();
      emitStateChange(previous, Object.keys(state), { ...meta, action: meta.action || 'reset' });
      return clone(state);
    },

    clearRescue(meta = {}) {
      return this.patch({
        currentModule: null,
        currentRoom: null,
        currentSituation: null,
        currentProblem: null,
        selectedRecommendation: null,
        scentMatch: null,
        continuation: null
      }, { ...meta, module: meta.module || 'rescue', action: meta.action || 'clear-rescue' });
    },

    subscribe(handler) {
      if (typeof handler !== 'function') return () => {};
      const listener = event => handler(event.detail);
      document.addEventListener('gooey:experience:statechange', listener);
      return () => document.removeEventListener('gooey:experience:statechange', listener);
    }
  };

  api.register = (name, initializer) => {
    if (!name || typeof initializer !== 'function') return;
    registry.set(name, initializer);
  };

  api.closeDisclosures = root => {
    qsa('[data-experience-drawer]', root).forEach(drawer => drawer.classList.remove('is-open'));
    qsa('[data-experience-trigger]', root).forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
  };

  api.openDisclosure = (root, trigger, drawer) => {
    if (!root || !trigger || !drawer) return;
    api.closeDisclosures(root);
    drawer.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    root.dispatchEvent(new CustomEvent('gooey:experience:open', {
      bubbles: true,
      detail: { module: root.dataset.gooeyExperience || '', drawer: drawer.id || '' }
    }));
  };

  api.closeDisclosure = (root, trigger, drawer, restoreFocus = false) => {
    if (!root || !drawer) return;
    drawer.classList.remove('is-open');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    }
  };

  api.init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-gooey-experience]')) roots.push(scope);
    roots.push(...qsa('[data-gooey-experience]', scope));

    roots.forEach(root => {
      if (root.dataset.experienceReady === 'true') return;
      const moduleName = root.dataset.gooeyExperience;
      const initializer = registry.get(moduleName);
      if (!initializer) return;
      root.dataset.experienceReady = 'true';
      initializer(root, api);
    });
  };

  const roomContext = snapshot => snapshot?.currentRoom || snapshot?.continuation?.room || null;
  const situationContext = snapshot => snapshot?.currentSituation || snapshot?.continuation?.situation || null;
  const recommendationContext = snapshot => snapshot?.continuation?.recommendation || snapshot?.selectedRecommendation || null;
  const hasRescueContext = snapshot => Boolean(roomContext(snapshot) || situationContext(snapshot) || snapshot?.currentProblem);

  function initHouse(root, experience) {
    const tabs = qsa('[data-house-tab]', root);
    const rooms = qsa('[data-house-room]', root);

    if (!tabs.length || !rooms.length) return;

    const roomForTab = tab => rooms.find(room => room.dataset.houseRoom === tab.dataset.houseTab);

    const roomMeta = room => {
      if (!room) return null;
      return {
        id: room.dataset.houseRoomKey || room.dataset.houseRoom || '',
        instanceId: room.dataset.houseRoom || '',
        name: room.dataset.houseRoomName || '',
        area: room.dataset.houseArea || '',
        problem: room.dataset.houseProblem || '',
        reference: room.dataset.houseReference || ''
      };
    };

    const situationMeta = element => {
      if (!element) return null;
      const id = element.dataset.houseSituationId || '';
      const label = element.dataset.houseSituationLabel || '';
      const problem = element.dataset.houseSituationProblem || '';
      if (!id && !label && !problem) return null;
      return { id, label, problem };
    };

    const drawerForTrigger = trigger => {
      const id = trigger?.getAttribute('aria-controls');
      if (!id) return null;
      return root.querySelector(`#${CSS.escape(id)}`);
    };

    const triggerForDrawer = drawer => {
      if (!drawer?.id) return null;
      return qsa('[data-experience-trigger]', root).find(trigger => trigger.getAttribute('aria-controls') === drawer.id) || null;
    };

    const recommendationMeta = drawer => {
      if (!drawer || drawer.dataset.experienceProductSelected !== 'true') return null;
      const link = drawer.querySelector('[data-house-fix-link]');
      const handle = drawer.dataset.houseRecommendedProduct || link?.dataset.houseProductHandle || '';
      const title = drawer.querySelector('.gooey-house__fix-title')?.textContent?.trim() || '';
      const url = link?.href || '';
      const destination = url;
      return { handle, title, url, destination };
    };

    const applyVisitedState = snapshot => {
      const visited = Array.isArray(snapshot?.visitedRooms) ? snapshot.visitedRooms : [];
      tabs.forEach(tab => {
        const room = roomForTab(tab);
        const meta = roomMeta(room);
        const seen = Boolean(meta && visited.some(item => item.id === meta.id || (item.name && item.name === meta.name)));
        tab.classList.toggle('is-visited', seen);
      });
    };

    const rememberRoom = (room, action, options = {}) => {
      const meta = roomMeta(room);
      if (!meta) return;
      const snapshot = experience.state.get();
      const visited = Array.isArray(snapshot.visitedRooms) ? snapshot.visitedRooms : [];
      const alreadyVisited = visited.some(item => item.id === meta.id || (item.name && item.name === meta.name));
      const visitedRooms = alreadyVisited ? visited : [...visited, meta];
      const patch = {
        currentModule: 'house',
        currentRoom: meta,
        visitedRooms
      };

      if (options.resetSituation) {
        patch.currentSituation = null;
        patch.currentProblem = meta.problem || null;
        patch.selectedRecommendation = null;
        patch.continuation = null;
      }

      const next = experience.state.patch(patch, { module: 'house', action });
      applyVisitedState(next);
    };

    const closeCards = restoreFocus => {
      qsa('[data-experience-drawer].is-open', root).forEach(drawer => {
        experience.closeDisclosure(root, triggerForDrawer(drawer), drawer, restoreFocus);
      });
    };

    const activate = (tab, focusTab = false, recordState = true) => {
      const target = tab.dataset.houseTab;
      const activeRoom = roomForTab(tab);
      closeCards(false);

      tabs.forEach(item => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });

      rooms.forEach(room => {
        const active = room.dataset.houseRoom === target;
        room.hidden = !active;
        room.classList.toggle('is-active', active);
      });

      if (recordState) rememberRoom(activeRoom, 'room-change', { resetSituation: true });
      if (focusTab) tab.focus();

      root.dispatchEvent(new CustomEvent('gooey:house:roomchange', {
        bubbles: true,
        detail: {
          room: roomMeta(activeRoom),
          instanceId: target,
          state: experience.state.get()
        }
      }));
    };

    tabs.forEach((tab, index) => {
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let next = index;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        else next = (index + 1) % tabs.length;

        activate(tabs[next], true);
      });
    });

    qsa('[data-experience-trigger]', root).forEach(trigger => {
      trigger.addEventListener('click', () => {
        const room = trigger.closest('[data-house-room]');
        const drawer = drawerForTrigger(trigger);
        if (!room || !drawer) return;

        const situation = situationMeta(trigger) || situationMeta(drawer);
        const recommendation = recommendationMeta(drawer);
        rememberRoom(room, 'hotspot-open');
        experience.state.patch({
          currentModule: 'house',
          currentSituation: situation,
          currentProblem: situation?.problem || roomMeta(room)?.problem || null,
          selectedRecommendation: recommendation
        }, { module: 'house', action: 'situation-view' });

        if (drawer.classList.contains('is-open')) {
          experience.closeDisclosure(root, trigger, drawer, true);
        } else {
          experience.openDisclosure(root, trigger, drawer);
          root.dispatchEvent(new CustomEvent('gooey:house:situationchange', {
            bubbles: true,
            detail: { room: roomMeta(room), situation, recommendation, state: experience.state.get() }
          }));
        }
      });
    });

    qsa('[data-experience-close]', root).forEach(button => {
      button.addEventListener('click', () => {
        const drawer = button.closest('[data-experience-drawer]');
        if (!drawer) return;
        experience.closeDisclosure(root, triggerForDrawer(drawer), drawer, true);
      });
    });

    qsa('[data-experience-cta]', root).forEach(link => {
      link.addEventListener('click', () => {
        const drawer = link.closest('[data-experience-drawer]');
        const room = link.closest('[data-house-room]');
        const recommendation = recommendationMeta(drawer);
        const currentRoom = roomMeta(room);
        const situation = situationMeta(drawer);
        experience.state.patch({
          currentModule: 'house',
          currentRoom,
          currentSituation: situation,
          currentProblem: situation?.problem || currentRoom?.problem || null,
          selectedRecommendation: recommendation,
          continuation: {
            source: 'house',
            mode: 'product',
            room: currentRoom,
            situation,
            recommendation,
            destination: link.href
          }
        }, { module: 'house', action: 'recommendation-cta' });
      });
    });

    qsa('[data-experience-drawer]', root).forEach(drawer => {
      if (drawer.dataset.experienceProductSelected !== 'true' || drawer.querySelector('[data-house-alt-scent]')) return;
      const fix = drawer.querySelector('.gooey-house__fix');
      if (!fix) return;
      const alt = create('a', 'gooey-house__alt-scent', 'Not quite your smell? Find your scent →');
      alt.href = scentFinderUrl();
      alt.dataset.houseAltScent = 'true';
      alt.addEventListener('click', () => {
        const room = drawer.closest('[data-house-room]');
        const currentRoom = roomMeta(room);
        const situation = situationMeta(drawer);
        const recommendation = recommendationMeta(drawer);
        experience.state.patch({
          currentModule: 'house',
          currentRoom,
          currentSituation: situation,
          currentProblem: situation?.problem || currentRoom?.problem || null,
          selectedRecommendation: recommendation,
          continuation: {
            source: 'house',
            mode: 'scent-quiz',
            room: currentRoom,
            situation,
            recommendation,
            destination: alt.href
          }
        }, { module: 'house', action: 'scent-quiz-cta' });
      });
      fix.insertAdjacentElement('afterend', alt);
    });

    root.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      closeCards(true);
    });

    const snapshot = experience.state.get();
    applyVisitedState(snapshot);
    const savedRoomId = snapshot.currentRoom?.id;
    const restoredTab = savedRoomId
      ? tabs.find(tab => roomMeta(roomForTab(tab))?.id === savedRoomId)
      : null;
    const activeTab = restoredTab || tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (activeTab && roomForTab(activeTab)) activate(activeTab, false, false);

    experience.state.subscribe(({ state: nextState }) => applyVisitedState(nextState));
  }

  const currentProductHandle = () => productHandleFromUrl(window.location.href);

  const recommendationMatchesPage = recommendation => {
    if (!recommendation) return false;
    const current = currentProductHandle();
    if (!current) return false;
    const recommended = recommendation.handle || productHandleFromUrl(recommendation.url || recommendation.destination || '');
    return Boolean(recommended && recommended === current);
  };

  const buildActionLink = (label, href, className = 'button button--outline') => {
    const link = create('a', className, label);
    link.href = href;
    return link;
  };

  const renderProductRescue = snapshot => {
    const productInfo = document.querySelector('.product-info');
    const existing = document.querySelector('[data-gooey-rescue-inline]');
    if (!productInfo) {
      existing?.remove();
      return;
    }

    const recommendation = recommendationContext(snapshot);
    if (!hasRescueContext(snapshot) || !recommendationMatchesPage(recommendation)) {
      existing?.remove();
      return;
    }

    const room = roomContext(snapshot);
    const situation = situationContext(snapshot);
    const source = snapshot.continuation?.source || 'house';
    const card = existing || create('aside', 'gooey-rescue-card');
    card.dataset.gooeyRescueInline = 'true';
    card.replaceChildren();

    const top = create('div', 'gooey-rescue-card__top');
    const kicker = create('p', 'gooey-rescue-card__kicker', 'GOOEY RESCUE');
    const route = create('span', 'gooey-rescue-card__route', room?.name || 'The house');
    top.append(kicker, route);

    const titleText = source === 'scent-quiz'
      ? `You checked the room. Then we checked your nose.`
      : `Gooey sent you here for ${room?.name || 'the situation'}.`;
    const title = create('strong', 'gooey-rescue-card__title', titleText);

    const detail = situation?.problem || snapshot.currentProblem || room?.problem || '';
    const copy = create('p', 'gooey-rescue-card__copy');
    if (situation?.label) {
      const label = create('strong', '', `${situation.label}. `);
      copy.append(label, document.createTextNode(detail || 'This is the trail you followed from the House.'));
    } else {
      copy.textContent = detail || 'This is the candle attached to the room you were fixing.';
    }

    const actions = create('div', 'gooey-rescue-card__actions');
    const back = buildActionLink('Back to the House', houseUrl(), 'button button--outline');
    const quiz = buildActionLink('Not quite your smell? Find your scent', scentFinderUrl(), 'gooey-rescue-card__text-link');
    quiz.addEventListener('click', () => {
      experienceToQuiz(snapshot, quiz.href, 'product-rescue-quiz');
    });
    actions.append(back, quiz);
    card.append(top, title, copy, actions);

    if (!existing) {
      const breadcrumbs = productInfo.querySelector('.breadcrumbs');
      if (breadcrumbs) breadcrumbs.insertAdjacentElement('afterend', card);
      else productInfo.prepend(card);
    }
  };

  const experienceToQuiz = (snapshot, destination, action = 'scent-quiz-cta') => {
    const room = roomContext(snapshot);
    const situation = situationContext(snapshot);
    const recommendation = recommendationContext(snapshot);
    api.state.patch({
      currentModule: snapshot.currentModule || 'rescue',
      currentRoom: room,
      currentSituation: situation,
      currentProblem: situation?.problem || snapshot.currentProblem || room?.problem || null,
      continuation: {
        source: snapshot.continuation?.source || 'house',
        mode: 'scent-quiz',
        room,
        situation,
        recommendation,
        destination
      }
    }, { module: 'rescue', action });
  };

  const renderQuizContext = snapshot => {
    const quiz = document.querySelector('[data-scent-quiz]');
    const existing = document.querySelector('[data-gooey-quiz-context]');
    if (!quiz || !hasRescueContext(snapshot)) {
      existing?.remove();
      return;
    }

    const room = roomContext(snapshot);
    const situation = situationContext(snapshot);
    const notice = existing || create('aside', 'gooey-rescue-quiz-context');
    notice.dataset.gooeyQuizContext = 'true';
    notice.replaceChildren();
    notice.append(
      create('span', 'gooey-rescue-quiz-context__kicker', 'ROOM CONTEXT SAVED'),
      create('strong', 'gooey-rescue-quiz-context__title', `Still fixing ${room?.name || 'the house'}?`),
      create('p', 'gooey-rescue-quiz-context__copy', situation?.problem || snapshot.currentProblem || 'Pick the scent you actually like. Gooey will keep the room problem attached to the result.')
    );

    if (!existing) {
      const shell = quiz.querySelector('.scent-quiz__shell');
      if (shell) shell.insertAdjacentElement('beforebegin', notice);
    }
  };

  const renderRescueDock = snapshot => {
    const onHouse = Boolean(document.querySelector('[data-gooey-experience="house"]'));
    const existing = document.querySelector('[data-gooey-rescue-dock]');
    if (onHouse || !hasRescueContext(snapshot)) {
      existing?.remove();
      return;
    }

    const room = roomContext(snapshot);
    const situation = situationContext(snapshot);
    const recommendation = recommendationContext(snapshot);
    const visitedCount = Array.isArray(snapshot.visitedRooms) ? snapshot.visitedRooms.length : 0;
    const dock = existing || create('div', 'gooey-rescue-dock');
    dock.dataset.gooeyRescueDock = 'true';
    dock.replaceChildren();

    const trigger = create('button', 'gooey-rescue-dock__trigger');
    trigger.type = 'button';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.append(
      create('span', 'gooey-rescue-dock__trigger-main', 'YOUR HOUSE'),
      create('span', 'gooey-rescue-dock__trigger-room', room?.name || `${visitedCount} rooms`)
    );

    const panel = create('aside', 'gooey-rescue-dock__panel');
    panel.hidden = true;
    panel.setAttribute('aria-label', 'Your Gooey Duck House context');

    const head = create('div', 'gooey-rescue-dock__head');
    const headCopy = create('div');
    headCopy.append(
      create('p', 'eyebrow', 'Your House'),
      create('h2', 'h3', room?.name || 'House evidence')
    );
    const close = create('button', 'gooey-rescue-dock__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close Your House');
    head.append(headCopy, close);

    const body = create('div', 'gooey-rescue-dock__body');
    if (situation?.label) body.append(create('strong', 'gooey-rescue-dock__situation', situation.label));
    body.append(create('p', 'gooey-rescue-dock__problem', situation?.problem || snapshot.currentProblem || room?.problem || 'Gooey kept the room you were working on.'));
    if (recommendation?.title) {
      const lead = create('div', 'gooey-rescue-dock__lead');
      lead.append(create('span', '', 'CURRENT LEAD'), create('strong', '', recommendation.title));
      body.append(lead);
    }
    body.append(create('p', 'gooey-rescue-dock__visited', `${visitedCount || 1} room${visitedCount === 1 ? '' : 's'} inspected this visit.`));

    const actions = create('div', 'gooey-rescue-dock__actions');
    const back = buildActionLink('Back to House', houseUrl(), 'button button--accent');
    const quiz = buildActionLink('Find another scent', scentFinderUrl(), 'button button--outline');
    quiz.addEventListener('click', () => experienceToQuiz(snapshot, quiz.href, 'dock-scent-quiz'));
    const clear = create('button', 'gooey-rescue-dock__clear', 'Clear this rescue');
    clear.type = 'button';
    clear.addEventListener('click', () => api.state.clearRescue({ module: 'rescue', action: 'dock-clear' }));
    actions.append(back, quiz, clear);

    panel.append(head, body, actions);
    dock.append(trigger, panel);

    const setOpen = open => {
      trigger.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      dock.classList.toggle('is-open', open);
    };
    trigger.addEventListener('click', () => setOpen(trigger.getAttribute('aria-expanded') !== 'true'));
    close.addEventListener('click', () => setOpen(false));
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.focus();
      }
    });

    if (!existing) document.body.append(dock);
  };

  const refreshRescueLayer = snapshot => {
    renderProductRescue(snapshot);
    renderQuizContext(snapshot);
    renderRescueDock(snapshot);
  };

  const initRescueLayer = () => {
    if (api.rescueReady) return;
    api.rescueReady = true;
    refreshRescueLayer(api.state.get());
    api.state.subscribe(({ state: nextState }) => refreshRescueLayer(nextState));
  };

  api.register('house', initHouse);
  window.GooeyExperience = api;

  const boot = () => {
    api.init();
    initRescueLayer();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', event => {
    api.init(event.target);
    refreshRescueLayer(api.state.get());
  });
})();
